import React, { useMemo } from 'react';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { determinePossibleSecondaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { doesFilterStoreValueContainItem } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { SearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import { RangeSliderAndTextInputs } from 'apps/sigma2-ui/src/filters-v2/components/RangeSliderAndTextInputs';
import { inferTypeForFilterStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const SecondaryFilterContent = ({
  stagingSecondaryFilters,
  feature,
  focusArea,
  setStagingSecondaryFilter,
  secondaryFilterKey,
  secondaryFilterDefinitions,
  myObservation,
  analysisName,
  dashboardGlobalFilters,
}: {
  secondaryFilterKey: string;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  secondaryFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
  stagingSecondaryFilters: SecondaryFilterSelection[];
  setStagingSecondaryFilter: (params: {
    secondaryFilterKey: string;
    value: FilterStoreValue;
  }) => void;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
  dashboardGlobalFilters: FilterDefinitionForExtrasArray;
}) => {
  const language = useLanguage();

  return useMemo(() => {
    const definition = secondaryFilterDefinitions.find(
      (definition) => definition.columnHeaderValue === secondaryFilterKey,
    );

    if (!definition) {
      return null;
    }

    const displayType =
      definition.displayType ?? FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE;

    const globalFilterValueForColumn = dashboardGlobalFilters.find(
      (globalFilter) => globalFilter.columnHeaderValue === secondaryFilterKey,
    );

    if (
      displayType === 'select_single' ||
      displayType === 'select_multi' ||
      globalFilterValueForColumn
    ) {
      const possibleValues = determinePossibleSecondaryFilterValues({
        secondaryFilterKey,
        feature,
        focusArea,
        myObservation,
        stagingSecondaryFilters,
        analysisName,
        dashboardGlobalFilters,
      });

      return (
        <SearchableFilterListBox
          disabled={Boolean(globalFilterValueForColumn)}
          items={possibleValues.map((value) => {
            // if the value has a display mapped value, use that for the display instead
            const displayValue =
              definition.optionalDisplayValues?.[value] ?? value;

            const selected = globalFilterValueForColumn
              ? globalFilterValueForColumn.values.includes(value)
              : stagingSecondaryFilters.some(
                  (item) =>
                    item.secondaryFilterKey === secondaryFilterKey &&
                    doesFilterStoreValueContainItem(item.value, value),
                );

            return {
              translatedContent: language.dangerousUserInput({
                value: displayValue,
              }),
              value,
              selected,
            };
          })}
          multiple={displayType === 'select_multi'}
          onChange={(newItems) => {
            setStagingSecondaryFilter({
              secondaryFilterKey,
              value: newItems,
            });
          }}
        />
      );
    }

    if (displayType === 'range_scalar') {
      // TODO: Currently, it is not possible for global filters to be of range type, so we do not need to disable this component based on that.
      //       If we ever add that functionality, we will need to add that logic here.
      const current = stagingSecondaryFilters.find(
        (item) => item.secondaryFilterKey === secondaryFilterKey,
      );

      if (!current) {
        return null;
      }

      const infer = inferTypeForFilterStore(current.value);

      if (infer.type !== '[number, number]') {
        return null;
      }

      const numericColumn = myObservation?.numericColumns?.[secondaryFilterKey];

      if (!numericColumn) {
        return null;
      }

      return (
        <RangeSliderAndTextInputs
          startLabelTranslatedContent={language.startRangeLabel({
            fieldDisplayName: definition.displayName,
          })}
          minimum={numericColumn.minValue}
          maximum={numericColumn.maxValue}
          precision={definition.displayTypePrecision ?? 0}
          onChange={([newStartValue, newEndValue]) => {
            setStagingSecondaryFilter({
              secondaryFilterKey,
              value: [newStartValue, newEndValue],
            });
          }}
          startValue={infer.numberRange[0]}
          endValue={infer.numberRange[1]}
          endLabelTranslatedContent={language.endRangeLabel({
            fieldDisplayName: definition.displayName,
          })}
        />
      );
    }
    return null;
  }, [
    feature,
    focusArea,
    myObservation,
    analysisName,
    secondaryFilterDefinitions,
    secondaryFilterKey,
    setStagingSecondaryFilter,
    stagingSecondaryFilters,
    language,
    dashboardGlobalFilters,
  ]);
};
